import { useEffect, useMemo } from 'react';
import axios from 'axios';
import { useQueries, useQuery } from 'react-query';
import { useDispatch, useSelector } from 'react-redux';

// types
import {
  OKVItem,
  OKVJsonContent,
  OKVProduct,
  OKVTile,
  OKVCategory,
  OKVItemType,
  CategoryTreeRoot,
} from './types';

// utils
import getEndpoint from 'utils/endpoints';
import { fetchProducts } from '../../Product/actions';
import { useIsPreview } from '../../../utils/hooks/useIsPreview';
import { catalogBrandNameSelector } from '../../../utils/selectors/globalsSelectors';

const uniq = <T,>(items: T[]): T[] => Array.from(new Set(items));

// we need to fetch the whole category tree for each market to find the parent of the category, series or product
// for future implementations ask the mbp team if they can adjust the api calls (pass parent category in one call)
const useFetchCategoryTree = (catalogLocales: string[], brand) => {
  const categoriesQueries = useMemo(
    () =>
      catalogLocales.map((locale) => ({
        refetchOnMount: false,
        refetchOnWindowFocus: false,
        queryKey: ['categoryTree', locale],
        queryFn: () =>
          axios
            .get<CategoryTreeRoot>(
              `${getEndpoint('categories', {})}/v1/${locale}/categories${
                brand ? `?brand=${brand}` : ''
              }`,
            )
            .then((res) => res.data),
      })),
    [catalogLocales, brand],
  );
  const categoryTree = useQueries(categoriesQueries);

  // to fix useQueries rerender bug:
  // https://github.com/TanStack/query/issues/3049
  const indicatorTimestamp = categoryTree.reduce((acc, cur) => acc + cur.dataUpdatedAt, 0);
  const categoryTreeCached = useMemo(
    () => categoryTree,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [indicatorTimestamp],
  );
  return categoryTreeCached;
};

const item2tile = (
  item: OKVItem,
  categoriesByLanguage: Record<string, CategoryTreeRoot | undefined>,
  categoriesStore?: Record<string, OKVCategory>,
): OKVTile | OKVItem => {
  const itemExistsInCatStore = Boolean(item?.identifier && categoriesStore?.[item.identifier]);

  if (itemExistsInCatStore) {
    let imageURL;
    const category = categoriesStore![item.identifier];
    const isSeries = category?.categoryType === OKVItemType.Series;

    // if we want to display a series tiles we have to use an ambience image (small, medium or large)
    if (category.categoryType === OKVItemType.Series) {
      imageURL = category?.images?.[1]?.small ?? category?.images?.[0]?.thumbnail;
    } else {
      imageURL = category?.images?.[0]?.thumbnail;
    }

    return {
      ...item,
      product: {
        name: isSeries
          ? category?.name
          : getParentInCategoryTree(categoriesByLanguage[item.catalogLocale], item.identifier),
        pictureObject: {
          url: imageURL,
        } as PictureObject,
        subtitle: isSeries ? category?.slogan : category?.name,
      } as OKVProduct,
      filterProductThumbnails: () => null,
      total: category.total,
    } as OKVTile;
  } else if (categoriesStore) {
    // store is loaded and item doesn't exist in it
    return { ...item, product: { deprecated: true } } as OKVTile;
  }

  // store is not yet loaded
  return item;
};

const getParentInCategoryTree = (categoryTree, identifier, parentIdentifier?, parentName?) => {
  if (categoryTree?.id === identifier && parentIdentifier && parentName) {
    return parentName;
  }
  if (categoryTree?.subCategories) {
    let parent;
    for (const category of categoryTree.subCategories) {
      parent = getParentInCategoryTree(category, identifier, categoryTree?.id, categoryTree?.name);
      if (parent) return parent;
    }
  }
  return null;
};

const getProductCount = async (cat: OKVItem, brand): Promise<number | undefined> => {
  return (
    await axios.get(
      `${getEndpoint('catalog', {})}/products?lang=${cat.catalogLocale}${
        brand ? `&brand=${brand}` : ''
      }&responseSize=list&limit=1&filter.query=categories.id:subtree(%22${cat.identifier}%22)`,
    )
  ).data?.total;
};

export const useFetchAssets = ({
  data,
  hideCount = false,
}: {
  data: OKVJsonContent[];
  hideCount?: boolean;
}): { data: OKVJsonContent<OKVItem | OKVTile>[] } => {
  const dispatch = useDispatch();
  const isPreview = useIsPreview();
  const brandName = useSelector(catalogBrandNameSelector);

  const categoryItems = useMemo(() => {
    return uniq(
      data
        .flatMap(({ items }) => items)
        .filter((item) => item.type === OKVItemType.Category || item.type === OKVItemType.Series),
    );
  }, [data]);

  const locales = useMemo(() => {
    return uniq(categoryItems.map((item) => item.catalogLocale));
  }, [categoryItems]);

  const categories = useFetchCategoryTree(locales, brandName);
  const categoriesByLanguage = useMemo(() => {
    return locales.reduce(
      (acc, locale, index) => {
        acc[locale] = categories[index]?.data;
        return acc;
      },
      {} as Record<string, CategoryTreeRoot | undefined>,
    );
  }, [categories, locales]);

  const { data: categoryStore } = useQuery(
    [categoryItems, hideCount],
    async () => {
      const resp = await Promise.all<{ okvCategory: OKVCategory }>(
        categoryItems.map((cat) =>
          (async () => {
            const okvCategoryResp = await axios.get<OKVCategory>(
              `${getEndpoint('categories', {})}/v1/${cat.catalogLocale}/categories?rootId=${
                cat.identifier
              }&depth=0&contentFilter=products&brand=${brandName}`,
            );
            let total: number | undefined = undefined;
            if (!hideCount) {
              total = await getProductCount(cat, brandName);
            }
            return {
              okvCategory: { ...okvCategoryResp.data, total },
            };
          })(),
        ),
      );

      return resp.reduce(
        (acc, { okvCategory }) => {
          if (okvCategory?.id) {
            acc[okvCategory.id] = okvCategory;
          }
          return acc;
        },
        {} as Record<string, OKVCategory>,
      );
    },
    { refetchOnWindowFocus: false, refetchOnMount: false },
  );

  const productItems = useMemo(() => {
    return uniq(
      data.flatMap(({ items }) => items).filter((item) => item.type === OKVItemType.Product),
    );
  }, [data]);

  useEffect(() => {
    // filter unqiue product items by catalog and call the fetch action
    const productsByCatalog: Record<string, Set<string>> = productItems.reduce((catalog, item) => {
      if (!catalog[item.catalogLocale]) {
        catalog[item.catalogLocale] = new Set();
      }
      catalog[item.catalogLocale].add(item.identifier);
      return catalog;
    }, {});

    Object.entries(productsByCatalog).forEach(([catalog, products]) => {
      dispatch(fetchProducts(Array.from(products), '', catalog) as any);
    });
  }, [dispatch, productItems]);

  const productCatalogs = useSelector((state: AppState) => state.productCatalogs);

  return {
    data: data.map((parent) => {
      return {
        ...parent,
        items: parent.items
          .map((item) => {
            if (item.type !== OKVItemType.Product) {
              return item2tile(item, categoriesByLanguage, categoryStore);
            } else if (productCatalogs?.[item.catalogLocale]?.products?.[item.identifier]) {
              const product = productCatalogs[item.catalogLocale].products[item.identifier];
              return {
                product: {
                  ...product,
                  name: product?.categories?.[0]?.name,
                  subtitle: product?.name,
                },
                linkType: 'product_detail_page',
                catalogLanguage: item.catalogLocale,
                type: item.type,
                identifier: item.identifier,
              } as unknown as OKVItem;
            } else if (Object.keys(productCatalogs?.[item.catalogLocale]?.products || {}).length) {
              return { ...item, product: { deprecated: true } };
            }

            return item;
          })
          .flat()
          .filter((item: OKVTile | OKVItem) => isPreview || !item.product?.deprecated) as (
          | OKVTile
          | OKVItem
        )[],
      };
    }),
  };
};
